import { Fragment } from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";

import { cn } from "@/lib/utils";
import { useIsScrolled } from "@/hooks/use-is-scrolled";

export interface Crumb {
  label: string;
  to: string;
}

interface PageHeaderProps {
  title: React.ReactNode;
  breadcrumbs?: Crumb[];
  actions?: React.ReactNode;
}

export function PageHeader({ title, breadcrumbs, actions }: PageHeaderProps) {
  const hasBreadcrumbs = !!breadcrumbs?.length;
  const isScrolled = useIsScrolled();

  // The top margin keeps the title off the screen edge and scrolls away once
  // the header sticks.
  return (
    <header
      data-scrolled={isScrolled || undefined}
      className={cn(
        "sticky top-[var(--top-bar-height,3.5rem)] z-20 -mx-4 mt-3 flex min-h-12 items-center justify-between gap-4 bg-background/85 px-4 py-2 backdrop-blur-sm sm:-mx-6 sm:px-6 md:top-0 md:mt-5",
        "after:pointer-events-none after:absolute after:inset-x-0 after:bottom-0 after:h-px after:bg-border after:opacity-0 after:transition-opacity after:duration-300 data-scrolled:after:opacity-100"
      )}
    >
      <div className="flex min-w-0 items-center">
        {hasBreadcrumbs && (
          <nav aria-label="Breadcrumb" className="-ml-1.5 flex min-w-0 items-center">
            {breadcrumbs.map((crumb, index) => (
              <Fragment key={crumb.to}>
                <Link
                  to={crumb.to}
                  className={cn(
                    "relative min-w-0 rounded-md px-1.5 py-1 text-[13px] font-medium text-muted-foreground transition-colors before:absolute before:inset-x-0 before:-inset-y-2.5 hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    index < breadcrumbs.length - 1 ? "hidden sm:flex" : "flex"
                  )}
                >
                  <span className="truncate">{crumb.label}</span>
                </Link>
                <ChevronRight
                  aria-hidden="true"
                  className={cn(
                    "size-3.5 shrink-0 text-muted-foreground/60",
                    index < breadcrumbs.length - 1 && "hidden sm:block"
                  )}
                />
              </Fragment>
            ))}
          </nav>
        )}
        <h1
          className={cn(
            "min-w-0 truncate",
            hasBreadcrumbs
              ? "max-w-[70%] shrink-0 px-1.5 text-[13px] font-medium"
              : "text-lg font-semibold tracking-[-0.01em]"
          )}
        >
          {title}
        </h1>
      </div>
      {actions && (
        <div className="flex shrink-0 items-center gap-2">{actions}</div>
      )}
    </header>
  );
}

interface PageTitleProps {
  title: React.ReactNode;
  description?: React.ReactNode;
  icon?: React.ReactNode;
  className?: string;
}

export function PageTitle({ title, description, icon, className }: PageTitleProps) {
  return (
    <div className={cn("flex items-center gap-3", className)}>
      {icon}
      <div className="min-w-0">
        <h2 className="text-2xl font-semibold tracking-[-0.01em] wrap-anywhere">
          {title}
        </h2>
        {description && (
          <p className="mt-1 text-sm text-muted-foreground">{description}</p>
        )}
      </div>
    </div>
  );
}
